import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Hammer, Briefcase, ClipboardList, CheckCircle, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet';

const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: Building2,
      title: 'Interior Fit-Out',
      description: 'Transform your commercial space with our comprehensive interior fit-out services. We handle everything from demolition to final finishes.',
      image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxjb21tZXJjaWFsJTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc2MTYwMjM1M3ww&ixlib=rb-4.1.0&q=85',
      features: [
        'Complete office renovations',
        'Partition walls and ceilings',
        'Electrical and lighting systems',
        'HVAC integration',
        'Flooring and finishes',
      ],
    },
    {
      icon: Hammer,
      title: 'Design-Build',
      description: 'Streamline your project with our integrated design-build approach, combining architectural design and construction under one roof.',
      image: 'https://customer-assets.emergentagent.com/job_build-marble/artifacts/mtpji2dh_WhatsApp%20Image%202025-11-08%20at%2021.01.29_8392183c.jpg',
      features: [
        'Conceptual design development',
        '3D visualization and rendering',
        'Value engineering',
        'Seamless construction execution',
        'Single point of contact',
      ],
    },
    {
      icon: Briefcase,
      title: 'General Contracting',
      description: 'Comprehensive general contracting services delivering quality construction projects from groundbreaking to completion.',
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80',
      features: [
        'Commercial construction management',
        'Site supervision and coordination',
        'Trade contractor management',
        'Quality assurance and safety',
        'Schedule and budget control',
      ],
    },
    {
      icon: ClipboardList,
      title: 'Project Management',
      description: 'Ensure your construction project stays on track with our comprehensive project management services from planning to completion.',
      image: 'https://customer-assets.emergentagent.com/job_build-marble/artifacts/5fm72sm9_Generated%20Image%20November%2010%2C%202025%20-%2011_59AM.png',
      features: [
        'Project planning and scheduling',
        'Budget management',
        'Quality control and inspections',
        'Subcontractor coordination',
        'Risk management',
      ],
    },
  ];

  return (
    <main className="min-h-screen">
      <Helmet>
        <title>Construction Services Toronto & GTA | Interior Fit-Out, Design-Build, General Contracting | Oracle</title>
        <meta name="description" content="Comprehensive commercial construction services in Toronto & GTA: interior fit-outs, design-build, general contracting, project management. Licensed contractor. Free consultation: (647) 631-8914." />
        <link rel="canonical" href="https://oracleprojectgroup.com/services" />
        <meta property="og:title" content="Construction Services Toronto & GTA | Oracle Project Group" />
        <meta property="og:description" content="Interior fit-out, design-build, and general contracting services for commercial spaces in Toronto & GTA." />
        <meta property="og:url" content="https://oracleprojectgroup.com/services" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Construction Services Toronto & GTA | Oracle Project Group" />
        <meta name="twitter:description" content="Interior fit-out, design-build, and general contracting services for commercial spaces in Toronto & GTA." />
        <meta name="keywords" content="interior fit-out Toronto, design-build contractor, general contracting GTA, construction services, commercial renovation Toronto, office renovation" />

        {/* Services Schema JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Commercial Construction Services Toronto & GTA",
            "description": "Professional construction services offered by Oracle Project Group in Toronto and the Greater Toronto Area",
            "url": "https://oracleprojectgroup.com/services",
            "numberOfItems": 3,
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "item": {
                  "@type": "Service",
                  "@id": "https://oracleprojectgroup.com/services#interior-fitout",
                  "name": "Interior Fit-Out Construction Toronto",
                  "description": "Complete interior construction and fit-out services for commercial spaces in Toronto & GTA. Transform your office, retail, or commercial space with expert craftsmanship.",
                  "provider": {
                    "@type": "GeneralContractor",
                    "name": "Oracle Project Group",
                    "url": "https://oracleprojectgroup.com"
                  },
                  "areaServed": ["Toronto", "Greater Toronto Area", "Mississauga", "Vaughan"],
                  "serviceType": "Interior Fit-Out Construction"
                }
              },
              {
                "@type": "ListItem",
                "position": 2,
                "item": {
                  "@type": "Service",
                  "@id": "https://oracleprojectgroup.com/services#design-build",
                  "name": "Design-Build Construction GTA",
                  "description": "Integrated design and construction services for streamlined commercial project delivery in the Greater Toronto Area. Single point of contact from concept to completion.",
                  "provider": {
                    "@type": "GeneralContractor",
                    "name": "Oracle Project Group",
                    "url": "https://oracleprojectgroup.com"
                  },
                  "areaServed": ["Toronto", "Greater Toronto Area", "Mississauga", "Vaughan"],
                  "serviceType": "Design-Build Construction"
                }
              },
              {
                "@type": "ListItem",
                "position": 3,
                "item": {
                  "@type": "Service",
                  "@id": "https://oracleprojectgroup.com/services#general-contracting",
                  "name": "General Contracting Toronto",
                  "description": "Comprehensive licensed general contracting services for commercial construction projects in Toronto. Expert project management, trade coordination, and quality assurance.",
                  "provider": {
                    "@type": "GeneralContractor",
                    "name": "Oracle Project Group",
                    "url": "https://oracleprojectgroup.com"
                  },
                  "areaServed": ["Toronto", "Greater Toronto Area", "Mississauga", "Vaughan"],
                  "serviceType": "General Contracting"
                }
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <header className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBpbnRlcmlvcnxlbnwwfHx8fDE3NjE2MDIzNTd8MA&ixlib=rb-4.1.0&q=85"
          alt="Services - Oracle Project Group construction excellence"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Our Services</h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            Leveraging our expertise, we take a unique collaborative approach for a seamless project flow, from start to finish.
          </p>
        </div>
      </header>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              const isEven = index % 2 === 0;
              return (
                <div
                  key={index}
                  className={`grid md:grid-cols-2 gap-12 items-center ${
                    isEven ? '' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={isEven ? 'order-1' : 'order-2'}>
                    <h2 className="text-4xl font-bold text-[#0F1115] mb-4">{service.title}</h2>
                    <div className="w-24 h-1 bg-[#C6A45B] mb-6"></div>
                    <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-8">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="text-[#D4AF37] flex-shrink-0 mt-1" size={20} />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      className="inline-flex items-center px-6 py-3 bg-[#D4AF37] text-white rounded-md hover:bg-[#C19A2B] transition-all btn-transition font-semibold"
                    >
                      Request a Consultation
                      <ArrowRight className="ml-2" size={20} />
                    </Link>
                  </div>
                  <div className={`relative h-96 rounded-lg overflow-hidden shadow-xl ${isEven ? 'order-2' : 'order-1'}`}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0F1115] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your construction needs and receive a comprehensive estimate.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-[#C6A45B] text-white rounded-md hover:bg-[#B8860B] transition-all btn-transition font-semibold text-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Services;
